import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Whether an element has come near the screen (within `margin`), and stays true once it has: for work worth
 * doing only for what is seen, such as fetching a picture for each row of a long table. Where the browser has
 * no IntersectionObserver (tests, very old browsers) it is true at once.
 */
export function useInView<T extends Element>(margin = "200px"): [(element: T | null) => void, boolean] {
  const [seen, setSeen] = useState(() => typeof IntersectionObserver === "undefined");
  const observer = useRef<IntersectionObserver | null>(null);

  const ref = useCallback(
    (element: T | null) => {
      observer.current?.disconnect();
      observer.current = null;
      if (!element || seen) return;
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setSeen(true);
            observer.current?.disconnect();
          }
        },
        { rootMargin: margin },
      );
      observer.current.observe(element);
    },
    [margin, seen],
  );

  useEffect(() => () => observer.current?.disconnect(), []);
  return [ref, seen];
}
